import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Button from '../../../../shared/components/button';
import MoreButtonPopover from '../../../../shared/components/more-button-popover';
import { MorePopupOption } from '../../../../shared/components/more-button-popover/option';
import SvgImage from '../../../../shared/components/svg-image';
import { i18n } from '../../../../shared/helpers/i18n';
import { ACTION, FEATURE } from '../../../../shared/helpers/logger-enums';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { logger } from '../../features/waiting-rooms';
import { admitAllWaitingRoomPeersAction, setWaitingRoomPeersSort } from '../../features/waiting-rooms/slice';
import { selectCanAdmitAll, selectIsWaitingRoomOverflow, selectWaitingRoomPeersSort } from '../../selectors';
import { AdmitParams, WaitingRoomPeersSort } from '../../types';
import styles from './waiting-room-peers-footer.css';

const admitAllIcon = <SvgImage imageName="admitAll" />;
const sortIcon = <SvgImage imageName="sort" />;

export const WaitingRoomPeersFooter = () => {
    const dispatch = useDispatch();
    const waitingRoomPeersSort = useSelector(selectWaitingRoomPeersSort);
    const isWaitingRoomOverflow = useSelector(selectIsWaitingRoomOverflow);
    const canAdmitAll = useSelector(selectCanAdmitAll);

    const onAdmitAllButtonClick = useCallback(() => {
        const params: AdmitParams = {};
        if (isWaitingRoomOverflow) {
            params.toLimit = true;
        }

        logger.startFeatureDelta({
            feature: FEATURE.WAITING_ROOM,
            action: ACTION.ALLOW_SEVERAL,
        });

        dispatch(admitAllWaitingRoomPeersAction(params));
    }, [dispatch, isWaitingRoomOverflow]);

    const sortButtonRef = useRef<HTMLDivElement>(null);
    const [isSortPopupVisible, setIsSortPopupVisible] = useState(false);

    const onSortButtonClick = useCallback(() => {
        setIsSortPopupVisible(!isSortPopupVisible);
    }, [isSortPopupVisible]);

    const onCloseSortPopup = useCallback(() => {
        setIsSortPopupVisible(false);
    }, []);

    const onSortClick = useCallback(
        (value: WaitingRoomPeersSort) => {
            const isSortedByTime = value === WaitingRoomPeersSort.Time;
            countMetrika('waiting_rooms', 'sorting_type_changed', isSortedByTime ? 'Time' : 'Name');
            dispatch(setWaitingRoomPeersSort(value));
        },
        [dispatch]
    );

    const sortItems = useMemo(() => {
        return [
            <MorePopupOption
                key={WaitingRoomPeersSort.Time}
                iconName="leftSort"
                content={i18n('participants-sidebar', 'waiting-room-sort-time')}
                title={i18n('participants-sidebar', 'waiting-room-sort-time')}
                onClick={() => onSortClick(WaitingRoomPeersSort.Time)}
                checked={waitingRoomPeersSort === WaitingRoomPeersSort.Time}
            />,
            <MorePopupOption
                key="alfa"
                iconName="alfaSort"
                content={i18n('participants-sidebar', 'waiting-room-sort-alfa')}
                title={i18n('participants-sidebar', 'waiting-room-sort-alfa')}
                onClick={() => onSortClick(WaitingRoomPeersSort.Alfa)}
                checked={waitingRoomPeersSort === WaitingRoomPeersSort.Alfa}
            />,
        ];
    }, [onSortClick, waitingRoomPeersSort]);

    return (
        <div className={styles.container}>
            {canAdmitAll && (
                <Button
                    className={styles.admitAllButton}
                    view="neutral"
                    size="md"
                    iconLeft={admitAllIcon}
                    onClick={onAdmitAllButtonClick}
                    width="max"
                >
                    {isWaitingRoomOverflow ? i18n('common', 'admit-to-limit') : i18n('common', 'admit-all')}
                </Button>
            )}
            <div ref={sortButtonRef}>
                <Button
                    className={styles.sortButton}
                    view="neutral"
                    size="md"
                    icon={sortIcon}
                    onClick={onSortButtonClick}
                />
            </div>
            <MoreButtonPopover
                items={sortItems}
                className={styles.sortPopup}
                offset={8}
                isOpen={isSortPopupVisible}
                onClose={onCloseSortPopup}
                placement={'top right'}
                triggerRef={sortButtonRef}
            />
        </div>
    );
};
